import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useCollapse } from 'react-collapsed';
import { useNavigate } from 'react-router-dom';
import {
  useConversationParams,
  useBreakpoint,
  useLeftPanelActions,
  useScrollToTask,
  useConversationLink,
  useComparingConversationsParams,
  useComparedScrollToTask,
  useGetUser,
  useMessageListRefActions,
  useImageMaskingPanelActions,
  useConversationsIsCollapsed,
  useResetCompareSkills,
  useSkillCommand,
  useActiveModel,
} from 'src/hooks';
import { ApiMinimumTask, Conversation } from 'src/types';
import { TaskItem } from '../TaskItem';
import { DEFAULT_CHAT_NAME } from 'src/constants';
import { ConversationTitle } from '../ConversationTitle';
import { useLazyGetConversationByIdQuery } from 'src/store/services';

type ConversationThreadProps = {
  conversation: Conversation;
  linkedConversation?: Conversation;
  selected?: boolean;
};

export const ConversationItem = ({
  conversation,
  linkedConversation,
  selected = false,
}: ConversationThreadProps) => {
  const navigate = useNavigate();
  const { user_id } = useGetUser();
  const { isMobile } = useBreakpoint();
  const { firstComparedConvId } = useComparingConversationsParams();
  const { currentConversationId } = useConversationParams();
  const { toggleLeftPanelExpanded } = useLeftPanelActions();
  const { activeThreadAnchor, setActiveThreadAnchor } = useScrollToTask();
  const { comparedActiveThreadAnchor, setComparedActiveThreadAnchor } =
    useComparedScrollToTask({ conversation, linkedConversation });
  const { getCurrentMessageListRef } = useMessageListRefActions();
  const { resetImageMaskingPanelInfo } = useImageMaskingPanelActions();
  const { isConversationListCollapsed } = useConversationsIsCollapsed();
  const { toggleActiveSkillCommand } = useSkillCommand();
  const { changeInputBoxActiveModel } = useActiveModel();

  const { onResetCompareSkills } = useResetCompareSkills();

  const [trigger] = useLazyGetConversationByIdQuery();

  const { conversation_id, tasks, linked_conversation_id } = conversation;

  const threadId = conversation_id || DEFAULT_CHAT_NAME;

  const [isCurrentThreadExpanded, setIsCurrentThreadExpanded] = useState<
    boolean | null
  >(null);

  const isExpanded = useMemo(
    () =>
      isCurrentThreadExpanded === null
        ? isConversationListCollapsed
        : isCurrentThreadExpanded,
    [isCurrentThreadExpanded, isConversationListCollapsed],
  );

  const { linkTo } = useConversationLink({
    conversationId: threadId,
    linkedConversationId: linked_conversation_id,
  });

  useEffect(() => {
    setIsCurrentThreadExpanded(null);
  }, [isConversationListCollapsed]);

  const { getCollapseProps, getToggleProps } = useCollapse({
    defaultExpanded: true,
    isExpanded,
  });

  const handleConversationTitleClick = async (e: MouseEvent) => {
    setActiveThreadAnchor(null);

    if (linked_conversation_id) {
      onResetCompareSkills();
      setComparedActiveThreadAnchor(null);
    }

    const isNotSameThread =
      threadId !== currentConversationId && threadId !== firstComparedConvId;

    if (!(isNotSameThread && isCurrentThreadExpanded) && !isMobile) {
      setIsCurrentThreadExpanded((prev) => !prev);
    }

    if (isNotSameThread) {
      toggleActiveSkillCommand(null);
      changeInputBoxActiveModel();
    }

    if (isMobile) {
      toggleLeftPanelExpanded(false);
    }

    resetImageMaskingPanelInfo();
  };

  const handleTaskClick = async ({
    messageId,
    taskId,
  }: {
    messageId: string;
    taskId: string;
  }) => {
    if (!conversation_id) {
      return;
    }

    if (linked_conversation_id) {
      setComparedActiveThreadAnchor(taskId);
      onResetCompareSkills();
    } else {
      setActiveThreadAnchor(taskId);
    }

    if (isMobile) {
      toggleLeftPanelExpanded(false);
    }

    if (!!conversation_id && conversation_id !== currentConversationId) {
      navigate(linkTo);
      toggleActiveSkillCommand(null);
      changeInputBoxActiveModel();
    }

    resetImageMaskingPanelInfo();

    const { messages } = await trigger(
      { conversation_id, user_id },
      true,
    ).unwrap();

    if (!!messages && messages.length > 0) {
      const currentIndex = messages.findIndex(
        (item) => item.message_id === messageId,
      );

      // TODO(olha): WIP Virtuoso doesn't work
      const messageListRef = getCurrentMessageListRef(conversation_id);

      setTimeout(() => {
        messageListRef.current?.scrollToItem({
          index: currentIndex - 1,
          align: 'start-no-overflow',
          behavior: 'smooth',
        });
      }, 250);
    }
  };

  const conversationTitleToggleProps = getToggleProps({
    onClick: handleConversationTitleClick,
  });

  const isCurrentComparedConversation =
    linked_conversation_id === firstComparedConvId;

  const activeTaskAnchor = linked_conversation_id
    ? comparedActiveThreadAnchor
    : activeThreadAnchor;

  return (
    <>
      <ConversationTitle
        isSelected={selected}
        isExpanded={isExpanded}
        setIsExpanded={setIsCurrentThreadExpanded}
        hasTasks={!!tasks?.length}
        conversation={conversation}
        toggleProps={conversationTitleToggleProps}
        activeThreadAnchor={activeTaskAnchor}
      />

      <div {...getCollapseProps()}>
        {tasks?.map((task: ApiMinimumTask, index) => (
          <TaskItem
            key={`task-${task.task_id}`}
            task={task}
            selected={
              !isCurrentComparedConversation
                ? activeTaskAnchor === task.task_id && selected
                : false
            }
            selectedConversation={selected}
            onClick={handleTaskClick}
          />
        ))}
      </div>
    </>
  );
};
